import type { TaskComment } from '../types';

type CommentRef = Pick<TaskComment, 'id' | 'parentId'>;

export function buildTaskCommentsById(comments: TaskComment[]): Map<string, CommentRef> {
  const m = new Map<string, CommentRef>();
  for (const c of comments) {
    if (c.id) m.set(c.id, c);
  }
  return m;
}

/** Walk `parentId` chain to the top-level comment id for this thread. */
export function getTaskCommentThreadRootId(
  comment: CommentRef,
  byId: Map<string, CommentRef>,
): string | null {
  let root: CommentRef = comment;
  let cur: CommentRef | undefined = comment;
  const seen = new Set<string>();

  while (cur?.parentId) {
    if (seen.has(cur.parentId)) break;
    seen.add(cur.parentId);
    const parent = byId.get(cur.parentId);
    if (!parent) return cur.parentId;
    root = parent;
    cur = parent;
  }

  return root.id ?? null;
}

/** Parent id written when posting a reply or inline question answer. */
export function parentIdForTaskCommentReply(
  replyTo: CommentRef,
  comments: TaskComment[],
): string | null {
  if (!replyTo.id) return null;
  return getTaskCommentThreadRootId(replyTo, buildTaskCommentsById(comments)) || replyTo.id;
}

/**
 * Group every reply (any depth) under its thread root for one-level UI rendering.
 * Existing nested replies (reply-to-reply) become visible under the root comment.
 */
export function groupTaskCommentRepliesByThreadRoot(
  comments: TaskComment[],
): Map<string, TaskComment[]> {
  const byId = buildTaskCommentsById(comments);
  const m = new Map<string, TaskComment[]>();

  for (const c of comments) {
    if (!c.parentId || !c.id) continue;
    const rootId = getTaskCommentThreadRootId(c, byId);
    if (!rootId || rootId === c.id) continue;
    const arr = m.get(rootId) || [];
    arr.push(c);
    m.set(rootId, arr);
  }

  m.forEach((arr, rootId) => {
    arr.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    m.set(rootId, arr);
  });

  return m;
}
